import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Chip,
  Drawer,
  FormControlLabel,
  IconButton,
  InputAdornment,
  MenuItem,
  Switch,
  TextField,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import LinkOutlinedIcon from "@mui/icons-material/LinkOutlined";
import GroupsOutlinedIcon from "@mui/icons-material/GroupsOutlined";
import PauseCircleOutlineIcon from "@mui/icons-material/PauseCircleOutline";
import QrCodeOutlinedIcon from "@mui/icons-material/QrCodeOutlined";
import TvOutlinedIcon from "@mui/icons-material/TvOutlined";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import RadioButtonUncheckedIcon from "@mui/icons-material/RadioButtonUnchecked";
import { useSnackbar } from "notistack";
import { isTrackerPublicLinkAvailable, SUBMISSION_MODES } from "./trackerStorage";
import { trackerFieldSx as fieldSx } from "./trackerFormStyles";

const PRIMARY = "var(--portal-theme-primary)";
const PRIMARY_SOFT =
  "color-mix(in srgb, var(--portal-theme-primary) 12%, white)";
const BORDER = "#e2e8f0";
const MUTED = "#64748b";
const SURFACE = "#f8fafc";

const switchSx = {
  "& .MuiSwitch-switchBase.Mui-checked": { color: PRIMARY },
  "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": {
    backgroundColor: PRIMARY,
  },
};

const HelperText = ({ children, sx }) => (
  <Typography
    sx={{ fontSize: 12, color: MUTED, lineHeight: 1.45, mt: 0.75, ...sx }}
  >
    {children}
  </Typography>
);

const FieldLabel = ({ children }) => (
  <Typography sx={{ mb: 0.85, fontWeight: 600, fontSize: 13, color: "#334155" }}>
    {children}
  </Typography>
);

const IconBadge = ({ children }) => (
  <Box
    sx={{
      width: 36,
      height: 36,
      borderRadius: "10px",
      bgcolor: PRIMARY_SOFT,
      color: PRIMARY,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      flexShrink: 0,
    }}
  >
    {children}
  </Box>
);

const Section = ({ icon, title, description, action, children, last }) => (
  <Box
    component="section"
    sx={{
      py: 3,
      px: 0,
      borderBottom: last ? "none" : `1px solid ${BORDER}`,
    }}
  >
    <Box
      sx={{
        display: "flex",
        alignItems: "flex-start",
        justifyContent: "space-between",
        gap: 2,
        mb: 2.25,
      }}
    >
      <Box sx={{ display: "flex", gap: 1.25, minWidth: 0 }}>
        <IconBadge>{icon}</IconBadge>
        <Box sx={{ minWidth: 0 }}>
          <Typography
            sx={{ fontWeight: 700, fontSize: 15.5, color: "#0f172a" }}
          >
            {title}
          </Typography>
          {description ? (
            <Typography
              sx={{
                mt: 0.4,
                fontSize: 13,
                color: MUTED,
                lineHeight: 1.5,
              }}
            >
              {description}
            </Typography>
          ) : null}
        </Box>
      </Box>
      {action ? <Box sx={{ flexShrink: 0 }}>{action}</Box> : null}
    </Box>
    {children}
  </Box>
);

const SettingPanel = ({ children, sx }) => (
  <Box
    sx={{
      p: 2,
      borderRadius: "12px",
      border: `1px solid ${BORDER}`,
      bgcolor: SURFACE,
      ...sx,
    }}
  >
    {children}
  </Box>
);

const SettingRow = ({ title, description, control, children }) => (
  <Box
    sx={{
      display: "flex",
      alignItems: "flex-start",
      justifyContent: "space-between",
      gap: 2,
    }}
  >
    <Box sx={{ flex: 1, minWidth: 0 }}>
      <Typography sx={{ fontWeight: 600, fontSize: 14, color: "#0f172a" }}>
        {title}
      </Typography>
      {description ? (
        <Typography sx={{ fontSize: 12.5, color: MUTED, mt: 0.35, lineHeight: 1.45 }}>
          {description}
        </Typography>
      ) : null}
      {children}
    </Box>
    {control ? <Box sx={{ flexShrink: 0, pt: 0.25 }}>{control}</Box> : null}
  </Box>
);

const ModeCard = ({ title, description, selected, onClick }) => (
  <Box
    component="button"
    type="button"
    onClick={onClick}
    sx={{
      width: "100%",
      textAlign: "left",
      border: "1.5px solid",
      borderColor: selected ? PRIMARY : BORDER,
      bgcolor: selected ? PRIMARY_SOFT : "#fff",
      borderRadius: "12px",
      p: 2,
      cursor: "pointer",
      boxShadow: selected
        ? `0 0 0 1px color-mix(in srgb, ${PRIMARY} 40%, white)`
        : "none",
      transition: "border-color 0.15s ease, background-color 0.15s ease",
      "&:hover": {
        borderColor: selected ? PRIMARY : "#cbd5e1",
      },
    }}
  >
    <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1.25 }}>
      {selected ? (
        <CheckCircleOutlineIcon sx={{ fontSize: 22, color: PRIMARY, mt: 0.1 }} />
      ) : (
        <RadioButtonUncheckedIcon sx={{ fontSize: 22, color: "#94a3b8", mt: 0.1 }} />
      )}
      <Box sx={{ minWidth: 0 }}>
        <Typography sx={{ fontWeight: 700, fontSize: 14, color: "#0f172a" }}>
          {title}
        </Typography>
        <Typography
          sx={{ mt: 0.5, fontSize: 13, color: MUTED, lineHeight: 1.45 }}
        >
          {description}
        </Typography>
      </Box>
    </Box>
  </Box>
);

const buildDraftFromTracker = (tracker) => ({
  coaches: tracker.coaches || [],
  submissionMode: tracker.submissionMode || SUBMISSION_MODES.UPDATE_EXISTING,
  publicSlug: tracker.publicSlug || tracker.id || "",
  settings: {
    requirePassword: false,
    password: "",
    submissionLimit: "",
    confirmationMessage: "",
    confirmationEmailTemplate: "",
    confirmationEmailEnabled: false,
    noMatchBehaviour: "create",
    acceptingSubmissions: true,
    excelExportEnabled: true,
    recordSubmittedBy: true,
    ...(tracker.settings || {}),
  },
  gymTv: {
    enabled: false,
    headline: tracker.name || "",
    showTop: 10,
    eligibleStatusIds: [],
    ...(tracker.gymTv || {}),
  },
  gymTvViews: tracker.gymTvViews || [],
  status: tracker.status || "draft",
});

const sanitizePublicSlug = (value) =>
  String(value || "")
    .toLowerCase()
    .replace(/[^a-z0-9-]+/g, "-")
    .replace(/-{2,}/g, "-");

const PublicLinkEditor = ({
  baseUrl,
  slug,
  onSlugChange,
  onCopy,
  onOpenQr,
  size = "small",
  label = "Public link",
}) => (
  <TextField
    fullWidth
    size="small"
    label={label}
    value={slug}
    onChange={(e) => onSlugChange(sanitizePublicSlug(e.target.value))}
    onBlur={() =>
      onSlugChange(sanitizePublicSlug(slug).replace(/^-+|-+$/g, ""))
    }
    placeholder="your-slug"
    inputProps={{
      "aria-label": label,
      spellCheck: false,
    }}
    InputProps={{
      startAdornment: (
        <InputAdornment
          position="start"
          sx={{
            mr: 0.75,
            maxWidth: { xs: "42%", sm: "none" },
            "& .MuiTypography-root": {
              fontSize: 12.5,
              color: MUTED,
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
              fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
            },
          }}
        >
          <Typography title={baseUrl}>{baseUrl}</Typography>
        </InputAdornment>
      ),
      endAdornment: (
        <InputAdornment position="end" sx={{ ml: 0.25 }}>
          <IconButton onClick={onCopy} title="Copy link" size={size} edge="end">
            <ContentCopyIcon fontSize="small" />
          </IconButton>
          <IconButton onClick={onOpenQr} title="QR code" size={size} edge="end">
            <QrCodeOutlinedIcon fontSize="small" />
          </IconButton>
        </InputAdornment>
      ),
    }}
    sx={{
      ...fieldSx,
      "& input": {
        fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
        fontSize: 13,
      },
    }}
  />
);

export default function PublishSettingsDrawer({
  open = false,
  variant = "drawer",
  hideHeader = false,
  tracker,
  publicUrl,
  onClose,
  onChange,
  onEditCoaches,
  onOpenQr,
}) {
  const { enqueueSnackbar } = useSnackbar();
  const [draft, setDraft] = useState(null);
  const isInline = variant === "inline";

  useEffect(() => {
    if (!tracker) return;
    if (!isInline && !open) return;
    setDraft(buildDraftFromTracker(tracker));
  }, [open, tracker, isInline]);

  const push = (next) => {
    setDraft(next);
    onChange?.({
      coaches: next.coaches,
      submissionMode: next.submissionMode,
      publicSlug: next.publicSlug,
      settings: next.settings,
      gymTv: next.gymTv,
      gymTvViews: next.gymTvViews,
      status: next.status,
    });
  };

  const updateSettings = (patch) => {
    push({
      ...draft,
      settings: { ...draft.settings, ...patch },
    });
  };

  const updateGymTv = (patch) => {
    push({
      ...draft,
      gymTv: { ...draft.gymTv, ...patch },
    });
  };

  const publicBaseUrl = `${window.location.origin}/join/`;
  const publicSlugValue =
    draft?.publicSlug || tracker?.publicSlug || tracker?.id || "";
  const composedPublicUrl = `${publicBaseUrl}${publicSlugValue}`;

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(composedPublicUrl || publicUrl);
      enqueueSnackbar("Public link copied", { variant: "success" });
    } catch {
      enqueueSnackbar("Could not copy link", { variant: "error" });
    }
  };

  const setPublicSlug = (publicSlug) => {
    push({
      ...draft,
      publicSlug,
    });
  };

  const statuses = tracker?.statuses || [];
  const isArchived = draft?.status === "archived";
  const isLive =
    !isArchived &&
    (draft?.status === "live" || draft?.status === "published");
  const publicLinkAvailable = isTrackerPublicLinkAvailable(tracker);

  const eligibleIds = useMemo(
    () => new Set(draft?.gymTv?.eligibleStatusIds || []),
    [draft]
  );

  if (!draft) return null;

  const submissionLimitUnlimited =
    draft.settings.submissionLimit === "" ||
    draft.settings.submissionLimit == null;

  const header = (
    <Box
      sx={{
        px: isInline ? 0 : 3,
        py: isInline ? 0 : 2.25,
        mb: isInline ? 1 : 0,
        borderBottom: isInline ? "none" : `1px solid ${BORDER}`,
        display: "flex",
        alignItems: "flex-start",
        justifyContent: "space-between",
        gap: 2,
        flexShrink: 0,
      }}
    >
      <Box sx={{ pr: isInline ? 0 : 1 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.25 }}>
          {isInline ? (
            <LinkOutlinedIcon fontSize="small" sx={{ color: PRIMARY }} />
          ) : null}
          <Typography
            sx={{
              fontWeight: 700,
              fontSize: isInline ? 16 : 20,
              color: "#0f172a",
            }}
          >
            Publish settings
          </Typography>
        </Box>
        <Typography
          sx={{
            mt: 0.75,
            fontSize: 13,
            color: MUTED,
            lineHeight: 1.5,
            ml: isInline ? 3.25 : 0,
          }}
        >
          Public link, duplicate handling and GymTV
        </Typography>
      </Box>
      {!isInline ? (
        <IconButton onClick={onClose} size="small" sx={{ mt: -0.5 }}>
          <CloseIcon />
        </IconButton>
      ) : null}
    </Box>
  );

  const inlineBody = (
    <Box
      sx={{
        width: "100%",
        display: "flex",
        flexDirection: "column",
        gap: 3,
      }}
    >
      <Box sx={{ display: "flex", flexDirection: "column", gap: 2.5 }}>
        {!publicLinkAvailable ? (
          <Typography sx={{ fontSize: 13, color: MUTED, lineHeight: 1.45 }}>
            Public link, password and submission limit are not available for
            GymRite members — set Tracker Access to Public on Basics for a
            shared URL.
          </Typography>
        ) : (
          <>
            <PublicLinkEditor
              baseUrl={publicBaseUrl}
              slug={publicSlugValue}
              onSlugChange={setPublicSlug}
              onCopy={copyLink}
              onOpenQr={onOpenQr}
              size="small"
              label="Public link"
            />

            {draft.settings.requirePassword ? (
              <>
                <Box
                  sx={{
                    display: "grid",
                    gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
                    gap: 2.5,
                    alignItems: "start",
                  }}
                >
                  <TextField
                    select
                    fullWidth
                    size="small"
                    label="Password protection"
                    value="protected"
                    onChange={(e) => {
                      const requirePassword = e.target.value === "protected";
                      updateSettings({
                        requirePassword,
                        ...(requirePassword ? {} : { password: "" }),
                      });
                    }}
                    sx={fieldSx}
                  >
                    <MenuItem value="open">No password</MenuItem>
                    <MenuItem value="protected">Password protected</MenuItem>
                  </TextField>
                  <TextField
                    fullWidth
                    size="small"
                    type="password"
                    label="Public access password"
                    placeholder="Set access password..."
                    value={draft.settings.password || ""}
                    onChange={(e) =>
                      updateSettings({ password: e.target.value })
                    }
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <LockOutlinedIcon
                            sx={{ fontSize: 18, color: MUTED }}
                          />
                        </InputAdornment>
                      ),
                    }}
                    sx={fieldSx}
                  />
                </Box>
                <Box
                  sx={{
                    display: "grid",
                    gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
                    gap: 2.5,
                    alignItems: "start",
                  }}
                >
                  <TextField
                    select
                    fullWidth
                    size="small"
                    label="Submissions limit"
                    value={
                      submissionLimitUnlimited ? "unlimited" : "limited"
                    }
                    onChange={(e) => {
                      if (e.target.value === "unlimited") {
                        updateSettings({ submissionLimit: "" });
                        return;
                      }
                      updateSettings({
                        submissionLimit:
                          draft.settings.submissionLimit || "500",
                      });
                    }}
                    sx={fieldSx}
                  >
                    <MenuItem value="unlimited">Unlimited submissions</MenuItem>
                    <MenuItem value="limited">Limited submissions</MenuItem>
                  </TextField>
                  {!submissionLimitUnlimited ? (
                    <TextField
                      fullWidth
                      size="small"
                      type="number"
                      label="Max submissions limit"
                      placeholder="e.g. 500"
                      value={draft.settings.submissionLimit ?? ""}
                      onChange={(e) =>
                        updateSettings({ submissionLimit: e.target.value })
                      }
                      inputProps={{ min: 1 }}
                      sx={fieldSx}
                    />
                  ) : null}
                </Box>
              </>
            ) : (
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: {
                    xs: "1fr",
                    sm: submissionLimitUnlimited ? "1fr 1fr" : "1fr 1fr 1fr",
                  },
                  gap: 2.5,
                  alignItems: "start",
                }}
              >
                <TextField
                  select
                  fullWidth
                  size="small"
                  label="Password protection"
                  value="open"
                  onChange={(e) => {
                    const requirePassword = e.target.value === "protected";
                    updateSettings({
                      requirePassword,
                      ...(requirePassword ? {} : { password: "" }),
                    });
                  }}
                  sx={fieldSx}
                >
                  <MenuItem value="open">No password</MenuItem>
                  <MenuItem value="protected">Password protected</MenuItem>
                </TextField>
                <TextField
                  select
                  fullWidth
                  size="small"
                  label="Submissions limit"
                  value={
                    submissionLimitUnlimited ? "unlimited" : "limited"
                  }
                  onChange={(e) => {
                    if (e.target.value === "unlimited") {
                      updateSettings({ submissionLimit: "" });
                      return;
                    }
                    updateSettings({
                      submissionLimit:
                        draft.settings.submissionLimit || "500",
                    });
                  }}
                  sx={fieldSx}
                >
                  <MenuItem value="unlimited">Unlimited submissions</MenuItem>
                  <MenuItem value="limited">Limited submissions</MenuItem>
                </TextField>
                {!submissionLimitUnlimited ? (
                  <TextField
                    fullWidth
                    size="small"
                    type="number"
                    label="Max submissions limit"
                    placeholder="e.g. 500"
                    value={draft.settings.submissionLimit ?? ""}
                    onChange={(e) =>
                      updateSettings({ submissionLimit: e.target.value })
                    }
                    inputProps={{ min: 1 }}
                    sx={fieldSx}
                  />
                ) : null}
              </Box>
            )}
          </>
        )}
      </Box>

      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
          gap: 2.5,
          alignItems: "start",
        }}
      >
        <TextField
          select
          fullWidth
          size="small"
          label="When someone submits again"
          value={draft.submissionMode || SUBMISSION_MODES.UPDATE_EXISTING}
          onChange={(e) =>
            push({
              ...draft,
              submissionMode: e.target.value,
            })
          }
          sx={fieldSx}
        >
          <MenuItem value={SUBMISSION_MODES.UPDATE_EXISTING}>
            Update the existing entry
          </MenuItem>
          <MenuItem value={SUBMISSION_MODES.NEW_ENTRY}>
            Create a new entry each time
          </MenuItem>
        </TextField>

        <TextField
          select
          fullWidth
          size="small"
          label="GymTV broadcast"
          value={draft.gymTv.enabled ? "enabled" : "disabled"}
          onChange={(e) => {
            const enabled = e.target.value === "enabled";
            const patch = { enabled };
            if (
              enabled &&
              !(draft.gymTv.eligibleStatusIds || []).length &&
              statuses.length
            ) {
              patch.eligibleStatusIds = statuses.map((s) => s.id);
            }
            if (enabled && !draft.gymTv.headline?.trim()) {
              patch.headline = tracker?.name || "";
            }
            updateGymTv(patch);
          }}
          sx={fieldSx}
        >
          <MenuItem value="enabled">Broadcast enabled</MenuItem>
          <MenuItem value="disabled">Broadcast disabled</MenuItem>
        </TextField>
      </Box>

      {/* {draft.gymTv.enabled ? (
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: 1.25,
            p: 1.5,
            borderRadius: "10px",
            border: `1px solid ${BORDER}`,
            bgcolor: SURFACE,
          }}
        >
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: { xs: "1fr", sm: "1fr 110px" },
              gap: 1.5,
            }}
          >
            <TextField
              size="small"
              label="Headline"
              placeholder={tracker?.name || "Tracker headline"}
              value={draft.gymTv.headline || ""}
              onChange={(e) => updateGymTv({ headline: e.target.value })}
              sx={fieldSx}
              fullWidth
            />
            <TextField
              size="small"
              type="number"
              label="Show top"
              value={draft.gymTv.showTop ?? 10}
              onChange={(e) =>
                updateGymTv({ showTop: Number(e.target.value) || 0 })
              }
              sx={fieldSx}
              fullWidth
            />
          </Box>
          <FieldLabel>Eligible statuses</FieldLabel>
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
            {statuses.length === 0 ? (
              <Typography sx={{ fontSize: 13, color: MUTED }}>
                Add statuses on the tracker first.
              </Typography>
            ) : (
              statuses.map((status) => {
                const selected = eligibleIds.has(status.id);
                return (
                  <Chip
                    key={status.id}
                    label={status.name || "Untitled"}
                    onClick={() => {
                      const next = new Set(eligibleIds);
                      if (next.has(status.id)) next.delete(status.id);
                      else next.add(status.id);
                      updateGymTv({
                        eligibleStatusIds: Array.from(next),
                      });
                    }}
                    sx={{
                      height: 30,
                      fontWeight: 600,
                      fontSize: 12.5,
                      border: `1px solid ${status.color || BORDER}`,
                      bgcolor: selected
                        ? `${status.color || PRIMARY}22`
                        : "#fff",
                      color: status.color || "#0f172a",
                    }}
                  />
                );
              })
            )}
          </Box>
        </Box>
      ) : null} */}

      {/* <Box>
        <Typography
          sx={{ fontWeight: 700, fontSize: 14, color: "#0f172a", mb: 1 }}
        >
          Intake
        </Typography>
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
            gap: 1.5,
            alignItems: "center",
          }}
        >
          <FormControlLabel
            control={
              <Switch
                size="small"
                checked={
                  publicLinkAvailable
                    ? draft.settings.acceptingSubmissions !== false
                    : false
                }
                disabled={!publicLinkAvailable}
                onChange={(e) =>
                  updateSettings({ acceptingSubmissions: e.target.checked })
                }
                sx={switchSx}
              />
            }
            label="Accepting submissions"
            sx={{
              m: 0,
              "& .MuiFormControlLabel-label": { fontSize: 13.5 },
            }}
          />
          <FormControlLabel
            control={
              <Switch
                size="small"
                checked={draft.status === "archived"}
                onChange={(e) => {
                  if (e.target.checked) {
                    push({
                      ...draft,
                      status: "archived",
                      settings: {
                        ...draft.settings,
                        acceptingSubmissions: false,
                      },
                    });
                  } else {
                    push({
                      ...draft,
                      status: isLive ? "live" : "draft",
                      settings: {
                        ...draft.settings,
                        acceptingSubmissions: true,
                      },
                    });
                  }
                }}
                sx={switchSx}
              />
            }
            label="Archive this journey"
            sx={{
              m: 0,
              "& .MuiFormControlLabel-label": { fontSize: 13.5 },
            }}
          />
        </Box>
      </Box> */}

      <TextField
        fullWidth
        size="small"
        multiline
        minRows={2}
        label="Confirmation message"
        value={draft.settings.confirmationMessage || ""}
        onChange={(e) =>
          updateSettings({ confirmationMessage: e.target.value })
        }
        sx={fieldSx}
      />

    </Box>
  );

  const drawerBody = (
    <Box
      sx={{
        flex: 1,
        overflow: "auto",
        px: 3,
        py: 0.5,
      }}
    >
      <Section
        icon={<LinkOutlinedIcon fontSize="small" />}
        title="Public link"
        description={
          publicLinkAvailable
            ? "Generated for Public trackers. Share it as a URL or a QR code."
            : "Not available for GymRite members trackers — people join through a programme."
        }
      >
        {!publicLinkAvailable ? (
          <Typography sx={{ fontSize: 13.5, color: MUTED, lineHeight: 1.5 }}>
            This tracker is for GymRite members. Switch Tracker Access to Public
            on Basics if you need a shared public URL.
          </Typography>
        ) : (
          <Box>
            <Box sx={{ mb: 2.5 }}>
              <PublicLinkEditor
                baseUrl={publicBaseUrl}
                slug={publicSlugValue}
                onSlugChange={setPublicSlug}
                onCopy={copyLink}
                onOpenQr={onOpenQr}
                size="medium"
              />
              <HelperText>
                Base URL is fixed — only the slug after /join/ can be changed.
              </HelperText>
            </Box>

            <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
              <Box>
                <TextField
                  fullWidth
                  size="small"
                  type="password"
                  label="Password"
                  placeholder={
                    draft.settings.requirePassword
                      ? "Enter password"
                      : "Turn on Required to set a password"
                  }
                  value={draft.settings.password || ""}
                  disabled={!draft.settings.requirePassword}
                  onChange={(e) =>
                    updateSettings({ password: e.target.value })
                  }
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end" sx={{ mr: 0.25 }}>
                        <FormControlLabel
                          control={
                            <Switch
                              size="small"
                              checked={Boolean(
                                draft.settings.requirePassword
                              )}
                              onChange={(e) => {
                                const requirePassword = e.target.checked;
                                updateSettings({
                                  requirePassword,
                                  ...(requirePassword ? {} : { password: "" }),
                                });
                              }}
                              sx={switchSx}
                            />
                          }
                          label="Required"
                          labelPlacement="start"
                          sx={{
                            m: 0,
                            ml: 0.5,
                            "& .MuiFormControlLabel-label": {
                              fontSize: 12.5,
                              fontWeight: 600,
                              color: MUTED,
                              whiteSpace: "nowrap",
                            },
                          }}
                        />
                      </InputAdornment>
                    ),
                  }}
                  sx={{
                    ...fieldSx,
                    "& .MuiOutlinedInput-root": {
                      ...fieldSx["& .MuiOutlinedInput-root"],
                      bgcolor: draft.settings.requirePassword
                        ? "#fff"
                        : SURFACE,
                      pr: 0.5,
                    },
                  }}
                />
                <HelperText>
                  Useful when the link is only meant for people who bought a
                  programme.
                </HelperText>
              </Box>

              <Box>
                <TextField
                  fullWidth
                  size="small"
                  label="Submission limit"
                  placeholder="No limit"
                  value={draft.settings.submissionLimit ?? ""}
                  onChange={(e) =>
                    updateSettings({ submissionLimit: e.target.value })
                  }
                  sx={fieldSx}
                />
                <HelperText>
                  The form closes itself once this many entries come in.
                </HelperText>
              </Box>

              <Box>
                <TextField
                  fullWidth
                  size="small"
                  multiline
                  minRows={3}
                  label="Confirmation message"
                  value={draft.settings.confirmationMessage || ""}
                  onChange={(e) =>
                    updateSettings({ confirmationMessage: e.target.value })
                  }
                  sx={fieldSx}
                />
                <HelperText>
                  Shown on screen and in the confirmation email.
                </HelperText>
              </Box>

              <SettingPanel>
                <SettingRow
                  title="Also send a confirmation email"
                  description="Optional email template. Leave off to show the on-screen message only."
                  control={
                    <Switch
                      checked={Boolean(draft.settings.confirmationEmailEnabled)}
                      onChange={(e) =>
                        updateSettings({
                          confirmationEmailEnabled: e.target.checked,
                        })
                      }
                      sx={switchSx}
                    />
                  }
                />
                {draft.settings.confirmationEmailEnabled && (
                  <TextField
                    fullWidth
                    size="small"
                    multiline
                    minRows={3}
                    label="Email template"
                    placeholder="Hi {{first_name}}, thanks for joining {{journey_name}}…"
                    value={draft.settings.confirmationEmailTemplate || ""}
                    onChange={(e) =>
                      updateSettings({
                        confirmationEmailTemplate: e.target.value,
                      })
                    }
                    sx={{ ...fieldSx, mt: 2 }}
                  />
                )}
              </SettingPanel>
            </Box>
          </Box>
        )}
      </Section>

      <Section
        icon={<GroupsOutlinedIcon fontSize="small" />}
        // title="Duplicate handling"
        description="We match a submission to a person on email first, then phone. If nobody matches, a new entry is always created."
      >
        <Box sx={{ display: "flex", flexDirection: "column", gap: 1.25 }}>
          <ModeCard
            title="Update the existing entry"
            description="A returning person keeps one record. New answers merge in and the submission count goes up."
            selected={
              draft.submissionMode === SUBMISSION_MODES.UPDATE_EXISTING
            }
            onClick={() =>
              push({
                ...draft,
                submissionMode: SUBMISSION_MODES.UPDATE_EXISTING,
              })
            }
          />
          <ModeCard
            title="Create a new entry each time"
            description="Every submission stands alone. Right for repeatable events and weekly challenges."
            selected={draft.submissionMode === SUBMISSION_MODES.NEW_ENTRY}
            onClick={() =>
              push({
                ...draft,
                submissionMode: SUBMISSION_MODES.NEW_ENTRY,
              })
            }
          />
        </Box>
      </Section>

      <Section
        icon={<FileDownloadOutlinedIcon fontSize="small" />}
        title="Excel export"
        description="Every Tracker supports Export to Excel of filtered Tracker Entries."
      >
        <SettingPanel>
          <SettingRow
            title="Enable Excel export"
            description="Export the current filtered entry set when live entries are available."
            control={
              <Switch
                checked={draft.settings.excelExportEnabled !== false}
                onChange={(e) =>
                  updateSettings({ excelExportEnabled: e.target.checked })
                }
                sx={switchSx}
              />
            }
          />
        </SettingPanel>
      </Section>

      <Section
        icon={<TvOutlinedIcon fontSize="small" />}
        title="GymTV broadcast"
        description="Put ranked entries on the big screen in the gym."
      >
        <SettingPanel sx={{ bgcolor: "#fff" }}>
          <SettingRow
            title="Show this tracker on GymTV"
            control={
              <Switch
                checked={Boolean(draft.gymTv.enabled)}
                onChange={(e) => {
                  const enabled = e.target.checked;
                  const patch = { enabled };
                  if (
                    enabled &&
                    !(draft.gymTv.eligibleStatusIds || []).length &&
                    statuses.length
                  ) {
                    patch.eligibleStatusIds = statuses.map((s) => s.id);
                  }
                  if (enabled && !draft.gymTv.headline?.trim()) {
                    patch.headline = tracker?.name || "";
                  }
                  updateGymTv(patch);
                }}
                sx={switchSx}
              />
            }
          />
        </SettingPanel>
      </Section>

      <Section
        icon={<PauseCircleOutlineIcon fontSize="small" />}
        title="Intake"
        description="Disable or archive stops new submissions. Existing journey history stays available for coaches, export and GymTV."
        last
      >
        <SettingPanel sx={{ bgcolor: "#fff", mb: 1.5 }}>
          <SettingRow
            title="Accepting submissions"
            description={
              publicLinkAvailable
                ? "Turn off to pause the public link without archiving the journey."
                : "Public intake is off for GymRite members trackers."
            }
            control={
              <Switch
                checked={
                  publicLinkAvailable
                    ? draft.settings.acceptingSubmissions !== false
                    : false
                }
                disabled={!publicLinkAvailable}
                onChange={(e) =>
                  updateSettings({ acceptingSubmissions: e.target.checked })
                }
                sx={switchSx}
              />
            }
          />
        </SettingPanel>
        <SettingPanel sx={{ bgcolor: "#fff" }}>
          <SettingRow
            title="Archive this journey"
            description="Marks the tracker archived and stops intake. You can still open history."
            control={
              <Switch
                checked={draft.status === "archived"}
                onChange={(e) => {
                  if (e.target.checked) {
                    push({
                      ...draft,
                      status: "archived",
                      settings: {
                        ...draft.settings,
                        acceptingSubmissions: false,
                      },
                    });
                  } else {
                    push({
                      ...draft,
                      status: isLive ? "live" : "draft",
                      settings: {
                        ...draft.settings,
                        acceptingSubmissions: true,
                      },
                    });
                  }
                }}
                sx={switchSx}
              />
            }
          />
        </SettingPanel>
      </Section>
    </Box>
  );

  if (isInline) {
    return (
      <Box sx={{ width: "100%" }}>
        {!hideHeader ? header : null}
        {inlineBody}
      </Box>
    );
  }

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      className="tracker-publish-drawer-root"
      PaperProps={{
        className: "tracker-publish-drawer MuiDrawer-paperAnchorRight",
        sx: {
          width: {
            xs: "100% !important",
            sm: "520px !important",
          },
          maxWidth: "100vw !important",
          border: "none",
          display: "flex",
          flexDirection: "column",
          bgcolor: "#fff",
        },
      }}
    >
      {header}
      {drawerBody}
    </Drawer>
  );
}
